import React, { useEffect, useRef, useCallback } from 'react';
import { Box, VStack, Text, Icon } from '@chakra-ui/react';
import { FiHardDrive, FiLogIn, FiLogOut, FiInfo } from 'react-icons/fi';
import { parseQRPayload } from 'utils/qrPayload';
import { getShellBg, WarmShapes, getModeAccent } from './scannerTheme';

// Ícono y etiqueta según el tipo de lector activo (Entrada/Salida/Consulta)
// — mismo mapeo de íconos que MODE_BADGE en ScannerShell.js (switch
// Entrada/Salida y botón de Consulta), para que el círculo pulsante de acá
// hable el mismo idioma visual en vez de mostrar siempre el genérico
// FiHardDrive. `mode` puede venir undefined (aún sin lector) → fallback.
const MODE_ICON  = { access_log: FiLogIn, exit_log: FiLogOut, info_only: FiInfo };
const MODE_LABEL = { access_log: 'Entrada', exit_log: 'Salida', info_only: 'Consulta' };

// Lector de código de barras USB (modo teclado/HID): al escanear, el
// dispositivo "escribe" el valor letra por letra y termina la lectura con
// Enter. Mantiene un input oculto siempre enfocado para capturar esa
// entrada — mismo contrato público (active/onScan) que QRScanner.js, para
// que ScannerShell pueda intercambiarlos sin más cambios.
const USBScanner = ({ active = true, onScan, isModalOpen = false, mode }) => {
  const accent = getModeAccent(mode);
  const modeIcon = MODE_ICON[mode] ?? FiHardDrive;
  const modeLabel = MODE_LABEL[mode];
  const inputRef     = useRef(null);
  const bufferRef    = useRef('');
  const lastScanRef  = useRef('');

  useEffect(() => {
    if (active && !isModalOpen) {
      bufferRef.current = '';
      if (inputRef.current) inputRef.current.value = '';
      inputRef.current?.focus();
    } else {
      bufferRef.current = '';
      inputRef.current?.blur();
    }
  }, [active, isModalOpen]);

  const handleBlur = useCallback(() => {
    if (active && !isModalOpen) inputRef.current?.focus();
  }, [active, isModalOpen]);

  const handleKeyDown = useCallback((e) => {
    if (!active) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      const raw = bufferRef.current;
      bufferRef.current = '';
      if (inputRef.current) inputRef.current.value = '';
      if (!raw) return;

      const token = parseQRPayload(raw);
      if (!token || token === lastScanRef.current) return;
      lastScanRef.current = token;
      setTimeout(() => { lastScanRef.current = ''; }, 3000);
      onScan(token);
      return;
    }

    // Ignora teclas de control (Shift, Tab, flechas, etc.) — solo acumula
    // caracteres imprimibles de una posición.
    if (e.key.length === 1) {
      bufferRef.current += e.key;
    }
  }, [active, onScan]);

  return (
    <Box position="absolute" inset={0} bg={getShellBg(mode)} overflow="hidden">
      <WarmShapes mode={mode} />
      <input
        ref={inputRef}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        aria-hidden="true"
        tabIndex={-1}
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          opacity: 0,
          padding: 0,
          border: 0,
          pointerEvents: 'none',
        }}
      />

      <VStack spacing={4} position="absolute" inset={0} align="center" justify="center" zIndex={1}>
        <Box bg="white" borderRadius="24px" boxShadow="0 16px 48px -8px rgba(35,30,26,.16)" px={10} py={8}>
          <VStack spacing={4}>
            <Box position="relative" w="96px" h="96px" display="flex" alignItems="center" justifyContent="center">
              <Box
                position="absolute" inset={0} borderRadius="full" border="3px solid" borderColor={accent}
                opacity={active ? 0.9 : 0.35}
                sx={active ? { animation: 'usbPulse 1.6s ease-in-out infinite' } : {}}
              />
              <Icon as={modeIcon} boxSize={9} color={active ? accent : 'ink.400'} />
            </Box>
            <Text fontSize="17px" color="ink.700" fontWeight="600">
              {active
                ? `Escanea con el lector USB${modeLabel ? ` — ${modeLabel}` : ''}`
                : 'Lector en pausa'}
            </Text>
          </VStack>
        </Box>
      </VStack>

      <style>{`
        @keyframes usbPulse {
          0%   { transform: scale(1);    opacity: 0.9; }
          50%  { transform: scale(1.25); opacity: 0.25; }
          100% { transform: scale(1);    opacity: 0.9; }
        }
      `}</style>
    </Box>
  );
};

export default USBScanner;
